import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play, X } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState, VideoCard, VideoGrid } from "@/components/VideoCard";
import { Poster, ProgressLine } from "@/components/Poster";
import { api, epLabel, fmtTime, pct } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Row = ({ title, to, children, testid }) => (
  <section className="mb-16" data-testid={testid}>
    <div className="mb-6 flex items-baseline justify-between">
      <h2 className="font-heading text-lg font-bold tracking-tight md:text-lg">{title}</h2>
      {to && <Link to={to} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900" data-testid={`${testid}-see-all`}>See all <ArrowRight size={12} /></Link>}
    </div>
    {children}
  </section>
);

const Hero = ({ v, onRemove }) => (
  <Link to={`/watch/${v.id}`} className="group relative mb-16 block overflow-hidden rounded-xl border border-zinc-200 bg-[#141416] animate-rise" data-testid="home-hero">
    <Poster id={v.id} type={v.kind === "episode" ? "still" : "backdrop"} alt={v.title} className="aspect-[21/9] w-full opacity-70 transition-transform duration-700 group-hover:scale-[1.02]" iconSize={56} />
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
    <div className="absolute bottom-0 left-0 right-0 p-6 text-white sm:p-10">
      <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-300">Pick up where you left off</p>
      <p className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">{v.kind === "episode" ? `${v.show} — ${epLabel(v)}` : v.title}</p>
      <p className="mt-1 font-mono text-xs text-white/70">{fmtTime(v.progress?.position)} of {fmtTime(v.progress?.duration || v.duration)}</p>
      <span className="mt-5 inline-flex h-11 items-center gap-2 rounded-full bg-white px-5 text-sm font-medium text-zinc-900 transition-transform group-hover:scale-[1.03]"><Play size={14} fill="currentColor" />Resume</span>
      <ProgressLine value={pct(v.progress)} className="mt-6 max-w-md rounded" />
    </div>
    <button onClick={(e) => { e.preventDefault(); onRemove(v.id); }} title="Remove from Continue watching" data-testid="home-hero-remove"
      className="absolute right-4 top-4 rounded-full bg-black/40 p-2 text-white/80 backdrop-blur hover:text-white"><X size={14} /></button>
  </Link>
);

export default function Home() {
  const { user } = useAuth();
  const [cont, setCont] = useState(null);
  const [recent, setRecent] = useState(null);
  const [movies, setMovies] = useState([]);
  useEffect(() => {
    api.get("/continue").then((r) => setCont(r.data));
    api.get("/videos", { params: { sort: "recent", limit: 12 } }).then((r) => setRecent(r.data));
    api.get("/videos", { params: { kind: "movie", status: "unwatched", sort: "title", limit: 12 } }).then((r) => setMovies(r.data.items));
  }, []);
  const remove = async (id) => { await api.delete(`/continue/${id}`); setCont((c) => c.filter((v) => v.id !== id)); };
  if (!cont || !recent) return <div className="h-64 animate-pulse rounded-xl bg-zinc-100" />;
  return (
    <div>
      <PageHeader eyebrow={`Good to see you, ${user?.name || ""}`} title="Tonight's screening" testid="home-title" sub={`${recent.total} videos on your shelf`} />
      {recent.total === 0 ? (
        <EmptyState title="Your shelf is empty" text={user?.role === "admin" ? "Connect the Plex server and choose a folder to import. Your movies, shows and videos will appear here." : "Nothing has been shared with you yet — ask an administrator to share their library."}
          action={user?.role === "admin" && <Link to="/sources" data-testid="home-connect-sources" className="inline-flex h-10 items-center rounded-full bg-zinc-900 px-5 text-sm text-white hover:bg-zinc-800">Connect a source</Link>} />
      ) : (
        <>
          {cont[0] && <Hero v={cont[0]} onRemove={remove} />}
          {cont.length > 1 && (
            <Row title="Continue watching" testid="home-continue-row">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">{cont.slice(1, 9).map((v, i) => <VideoCard key={v.id} video={v} index={i} wide />)}</div>
            </Row>
          )}
          <Row title="Recently added" to="/videos" testid="home-recent-row"><VideoGrid videos={recent.items} testid="home-recent-grid" /></Row>
          {movies.length > 0 && <Row title="Movies you haven't seen" to="/movies" testid="home-unwatched-row"><VideoGrid videos={movies} testid="home-unwatched-grid" /></Row>}
        </>
      )}
    </div>
  );
}
